import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import {
  ReadOnly,
  useEntityPermission,
  useMasterData,
  useLocalization,
} from '@ui-framework/index';
import { FormattedMessage } from 'react-intl';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useRef, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import PhoneInput from 'react-phone-number-input/input';
import 'react-phone-number-input/style.css';
import SmsOptInConfirmDialog from '../../Dialogs/SmsOptInConfirmDialog';
import getSMSSubscriptionAttributes from './lib/getSMSSubscriptionAttributes';
import normalizeUS from './lib/normalizeUS';
import formatUSPhoneNumber from './lib/formatUSPhoneNumber';
import PhoneTextField from './components/PhoneTextField';
import UserSMSDisclosure from './UserSMSDisclosure';
import ServiceProviderSMSDisclosure from './ServiceProviderSMSDisclosure';
import InsightChipWithToolTip from './InsightChipWithToolTip';

/*
 insightPurchased : Send "field name" from from Schema JSON and value comes from form values
 type: Send from Schema JSON  attributeas "INSIGHT_NUMBER_ONLY" or "INSIGHT_FULL" if phonenumber is insight number
 smsSubscriptionStatus:
  - If type "INSIGHT_NUMBER_ONLY" send "smsSubscriptionStatus" as component prop and value needs to be set on component prop
  - If type "INSIGHT_FULL" send "smsSubscriptionStatus" Send "field name" from from Schema JSON and value comes from form values

{
  "row": 1,
  "name": "customerPhone",
  "type": "INSIGHT_FULL",
  "label": "Phone",
  "control": "PhoneNumber",
  "required": true,
  "dependencies": {
  "smsOptedIn": "customerPhoneSmsOptedIn",
  "insightPurchased": "insightPurchased",
  "smsSubscriptionStatus": "customerPhoneSmsStatus"
  }
*/
const PhoneNumber = forwardRef(({ ...props }, ref) => {
  const { control, getValues, setValue } = useFormContext();
  const { isHidden, isReadOnly } = useEntityPermission();
  const masterData = useMasterData();

  const [showDisclosure, setShowDisclosure] = useState(false);
  const { formatMessage } = useLocalization();
  const [pendingChange, setPendingChange] = useState(null);

  const { dependencies = null } = props.field;
  const fieldType = props?.field?.type;

  const phoneTypeInsight = [
    'INSIGHT_NUMBER_ONLY',
    'INSIGHT_FULL',
    'INSIGHT_MANAGED',
  ].includes(fieldType);

  let smsSubscriptionStatus = null;
  let insightPurchased = false;
  let insightPurchasedAndTypeInsight = false;

  if (phoneTypeInsight) {
    smsSubscriptionStatus =
      getValues(dependencies?.smsSubscriptionStatus || 'undefined') || null;
    insightPurchased = dependencies?.insightPurchased
      ? getValues(dependencies?.insightPurchased || 'undefined')
      : false;
    insightPurchasedAndTypeInsight = insightPurchased;
  }

  const requiresSmsOptInConfirmDialog =
    insightPurchased && props.field?.confirmDialog === 'SmsOptInConfirmDialog';

  const currentValue = getValues(props?.field?.name);

  const helperText = props?.field?.helperText?.localeId
    ? formatMessage({
        id: props.field.helperText.localeId,
        defaultMessage: props.field.helperText.defaultMessage,
      })
    : '';

  const onConfirmDialog = () => {
    setPendingChange(current => {
      current?.apply();
      return null;
    });
  };

  const onCloseDialog = () => setPendingChange(null);

  const Disclosure =
    props?.field?.disclosure === 'SERVICE_PROVIDER'
      ? ServiceProviderSMSDisclosure
      : UserSMSDisclosure;

  if (isReadOnly(props?.permission, currentValue)) {
    let status = null;
    if (insightPurchasedAndTypeInsight && smsSubscriptionStatus !== null) {
      status = getSMSSubscriptionAttributes(
        smsSubscriptionStatus,
        formatUSPhoneNumber(masterData.saPhoneNumber),
        formatMessage,
      );
    }

    let managedPhoneAcceptsSMS = false;
    if (insightPurchased && fieldType === 'INSIGHT_MANAGED') {
      const cpSMSSubscriptionStatus =
        getValues(dependencies?.customerPhoneSmsStatus || 'undefined') || null;
      const spaSMSSubscriptionStatus =
        getValues(
          dependencies?.customerPhoneAlternateSmsSubscriptionStatus ||
            'undefined',
        ) || null;

      // TODO: Add the below 2 as a dependency in form json, the name should come as a dependency
      const customerPhoneSmsOptedIn = useRef(
        getValues('customerPhoneSmsOptedIn'),
      ).current;
      const customerAltPhoneSmsOptedIn = useRef(
        getValues('customerAltPhoneSmsOptedIn'),
      ).current;

      const isSubscribedPrimary =
        cpSMSSubscriptionStatus === 'SUBSCRIBED' && customerPhoneSmsOptedIn;

      const isSubscribedAlternate =
        spaSMSSubscriptionStatus === 'SUBSCRIBED' && customerAltPhoneSmsOptedIn;

      managedPhoneAcceptsSMS = isSubscribedPrimary || isSubscribedAlternate;
    }

    let phoneNumberToDisplay = currentValue
      ? formatUSPhoneNumber(currentValue)
      : currentValue;

    if (fieldType === 'INSIGHT_MANAGED' && !phoneNumberToDisplay) {
      phoneNumberToDisplay = (
        <FormattedMessage id="NOT_ACTIVE" defaultMessage="Not Active" />
      );
    }

    return (
      <Box>
        <Box>
          <ReadOnly name={props?.name || props?.field?.name}>
            {phoneNumberToDisplay}
            {insightPurchasedAndTypeInsight &&
              smsSubscriptionStatus !== null && (
                <InsightChipWithToolTip sx={{ ml: 1 }} status={status} />
              )}
            {insightPurchased && fieldType === 'INSIGHT_FULL' && (
              <Grid2 container sx={{ mt: 1 }}>
                <Grid2 xs>
                  <Typography variant="subtitle2">
                    <FormattedMessage
                      id="ACCEPTS_SMS"
                      defaultMessage="Accepts SMS"
                    />
                    :
                    <Typography
                      sx={{ ml: 0.4 }}
                      variant="subtitle2"
                      component="span"
                      color={
                        getValues(dependencies?.smsOptedIn || 'undefined')
                          ? 'success'
                          : 'error'
                      }>
                      {getValues(dependencies?.smsOptedIn || 'undefined') ? (
                        <FormattedMessage id="YES" defaultMessage="Yes" />
                      ) : (
                        <FormattedMessage id="NO" defaultMessage="No" />
                      )}
                    </Typography>
                  </Typography>
                </Grid2>
                <Grid2 xs>
                  <Disclosure
                    setShowDisclosure={setShowDisclosure}
                    showDisclosure={showDisclosure}
                  />
                </Grid2>
              </Grid2>
            )}

            {insightPurchased &&
              fieldType === 'INSIGHT_MANAGED' &&
              currentValue && (
                <Typography variant="subtitle2">
                  <FormattedMessage
                    id="ACCEPTS_SMS"
                    defaultMessage="Accepts SMS"
                  />
                  :
                  <Typography
                    sx={{ ml: 0.4 }}
                    variant="subtitle2"
                    component="span"
                    color={managedPhoneAcceptsSMS ? 'success' : 'error'}>
                    {managedPhoneAcceptsSMS ? 'Yes' : 'No'}
                  </Typography>
                </Typography>
              )}
          </ReadOnly>
        </Box>
      </Box>
    );
  }

  if (isHidden(props?.permission)) {
    return (
      <Box>
        <input
          type="hidden"
          name={props?.name || props?.field?.name}
          value={currentValue}
        />
      </Box>
    );
  }

  let insightProps = null;

  if (insightPurchasedAndTypeInsight) {
    insightProps = {
      chip: getSMSSubscriptionAttributes(
        smsSubscriptionStatus,
        formatUSPhoneNumber(masterData.saPhoneNumber),
        formatMessage,
      ),
      smsSubscriptionStatus,
    };
  }

  const smsOptedInValue = useWatch({
    name: props.field?.dependencies?.smsOptedIn || 'undefined',
  });

  useEffect(() => {
    if (
      smsOptedInValue === null &&
      insightPurchased &&
      fieldType === 'INSIGHT_FULL'
    ) {
      setValue(dependencies?.smsOptedIn, false, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    }
  }, [
    smsOptedInValue,
    setValue,
    props.field?.dependencies?.smsOptedIn,
    insightPurchased,
  ]);

  return (
    <Box ref={ref}>
      <Controller
        control={control}
        name={props.field.name}
        render={({ field: controllerField, fieldState }) => {
          return (
            <PhoneInput
              {...controllerField}
              country="US"
              insightProps={insightProps}
              id={props.field.name}
              helperText={helperText}
              international={false}
              inputComponent={PhoneTextField}
              error={fieldState.error?.message}
              value={controllerField.value ? `+1${controllerField.value}` : ''}
              onChange={val => controllerField.onChange(normalizeUS(val))}
            />
          );
        }}
      />
      {insightPurchased && fieldType === 'INSIGHT_FULL' && (
        <Controller
          control={control}
          name={props.field?.dependencies?.smsOptedIn}
          render={({ field: controllerField }) => {
            const {
              onChange,
              value: optedInValue,
              ...smsOptedInField
            } = controllerField;

            const currentOptedIn = [true, 'true'].includes(optedInValue);

            const handleSmsOptInChange = event => {
              const newOptedIn = [true, 'true'].includes(event.target.value);
              if (newOptedIn === currentOptedIn) return;
              const applyChange = () => onChange(newOptedIn);

              if (requiresSmsOptInConfirmDialog) {
                setPendingChange({ value: newOptedIn, apply: applyChange });
                return;
              }

              applyChange();
            };

            return (
              <Grid2 container sx={{ alignItems: 'center' }}>
                <Grid2>
                  <RadioGroup
                    sx={{ ml: 1 }}
                    row
                    {...smsOptedInField}
                    value={currentOptedIn ? 'true' : 'false'}
                    onChange={handleSmsOptInChange}>
                    <FormControlLabel
                      value="true"
                      control={<Radio size="small" sx={{ px: 0.2 }} />}
                      label={
                        <Tooltip
                          placement="top"
                          arrow
                          title={
                            <FormattedMessage
                              id="OPT_IN_TO_RECEIVE_SMS_NOTIFICATIONS"
                              defaultMessage="Opt-In to receive SMS notifications"
                            />
                          }>
                          <span>
                            <FormattedMessage
                              id="SMS_OPT_IN"
                              defaultMessage="SMS Opt-In"
                            />
                          </span>
                        </Tooltip>
                      }
                      sx={{ pr: 1 }}
                    />
                    <FormControlLabel
                      value="false"
                      control={
                        <Radio
                          size="small"
                          sx={{
                            px: 0.2,
                          }}
                        />
                      }
                      label={
                        <Tooltip
                          placement="top"
                          arrow
                          title={
                            <FormattedMessage
                              id="SMS_MESSAGES_WILL_NOT_BE_SENT"
                              defaultMessage="SMS messages will not be sent"
                            />
                          }>
                          <span>
                            <FormattedMessage
                              id="NO_SMS"
                              defaultMessage="No SMS"
                            />
                          </span>
                        </Tooltip>
                      }
                    />
                  </RadioGroup>
                </Grid2>
                <Grid2
                  sx={{
                    flexGrow: 1,
                    textAlign: 'right',
                    pr: 1,
                  }}>
                  <Disclosure
                    setShowDisclosure={setShowDisclosure}
                    showDisclosure={showDisclosure}
                  />
                </Grid2>
              </Grid2>
            );
          }}
        />
      )}
      {requiresSmsOptInConfirmDialog && (
        <SmsOptInConfirmDialog
          open={Boolean(pendingChange)}
          value={pendingChange?.value}
          onConfirm={onConfirmDialog}
          onCancel={onCloseDialog}
        />
      )}
    </Box>
  );
});

export default observer(PhoneNumber);
